import { useState, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../../utils/api';
import BuildingPageHeading from '../../components/BuildingPageHeading';
import {
  formatSsLabel,
} from '../../utils/unitDisplay';
import { sortSsUnitsByNumber } from '../../utils/unitIdFormat';
import { asList } from '../../utils/asList';
import { compareSsCode, sortBySsCode } from '../../utils/ssCodeSort';
import {
  filterInstancesForInspectionScope,
  isPartialInspectionScope,
} from '../../utils/inspectionScope';
import { formatAssigneeChip } from '../../components/UnitDefaultAssignee';
import { hasOpenInspection } from '../../utils/orderInspectionJobs';
import ScheduleToIqpModal from '../../components/ScheduleToIqpModal';
import SsUnitSubtypeWizard from '../../components/SsUnitSubtypeWizard';

function sameSsCode(a, b) {
  const norm = (v) =>
    String(v || '')
      .trim()
      .replace(/^SS\s+/i, '')
      .toLowerCase();
  return norm(a) === norm(b);
}

function BuildingUnits() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const ssFilter = searchParams.get('ss') || '';
  const setupList = searchParams.get('setup') === 'list';
  const [selectedCodes, setSelectedCodes] = useState(() => new Set());
  const [scheduleItems, setScheduleItems] = useState(null);

  const { data: building, isLoading: buildingLoading } = useQuery({
    queryKey: ['building', id],
    queryFn: async () => (await api.get(`/buildings/${id}`)).data,
  });

  const { data: instances = [] } = useQuery({
    queryKey: ['building', id, 'ss-instances'],
    queryFn: async () => {
      const response = await api.get(`/buildings/${id}/ss-instances`);
      return asList(response.data?.data ?? response.data);
    },
    enabled: !!id,
  });

  /** Operational units only — same set as app inspect / worker pack (ACTIVE|REPAIRING|DRAFT). */
  const { data: unitsAll = [], isLoading: unitsLoading } = useQuery({
    queryKey: ['building', id, 'ss-units', 'operational'],
    queryFn: async () => {
      const response = await api.get(`/buildings/${id}/ss-units`);
      return sortSsUnitsByNumber(asList(response.data?.data ?? response.data));
    },
    enabled: !!id,
  });

  const { data: buildingJobs = [] } = useQuery({
    queryKey: ['jobs', 'building', id],
    queryFn: async () => {
      const response = await api.get('/jobs', { params: { buildingId: id, limit: 200 } });
      return asList(response.data?.data ?? response.data);
    },
    enabled: !!id,
  });

  const scopedInstances = useMemo(() => {
    const fromApi = sortBySsCode(asList(instances));
    const filtered = filterInstancesForInspectionScope(fromApi, building);
    if (filtered.length > 0) return filtered;
    return sortBySsCode(
      filterInstancesForInspectionScope(
        asList(building?.ssInstancesInScope || building?.ssInstances || building?.ss_instances),
        building
      )
    );
  }, [instances, building]);

  const scopedSsCodes = useMemo(
    () =>
      new Set(
        scopedInstances.map((i) => String(i.ss_code || '').trim()).filter(Boolean)
      ),
    [scopedInstances]
  );

  const scopedInstanceIds = useMemo(
    () => new Set(scopedInstances.map((i) => Number(i.id))),
    [scopedInstances]
  );

  const scopedUnitsAll = useMemo(
    () =>
      asList(unitsAll).filter((u) => {
        const code = String(u.ss_code || '').trim();
        if (code && scopedSsCodes.has(code)) return true;
        return scopedInstanceIds.has(Number(u.building_ss_instance_id));
      }),
    [unitsAll, scopedSsCodes, scopedInstanceIds]
  );

  const instByCode = useMemo(() => {
    const map = new Map();
    scopedInstances.forEach((inst) => {
      if (!inst || typeof inst !== 'object') return;
      const key = String(inst.ss_code || '').trim();
      if (!key) return;
      map.set(key, { ...inst });
    });
    return map;
  }, [scopedInstances]);

  const jobsBySs = useMemo(() => {
    const map = new Map();
    asList(buildingJobs).forEach((j) => {
      const key = String(j.ss_code || '').trim();
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(j);
    });
    return map;
  }, [buildingJobs]);

  const ssRows = useMemo(() => {
    const byCode = new Map();
    for (const inst of scopedInstances) {
      const key = String(inst.ss_code || '').trim();
      if (!key) continue;
      byCode.set(key, {
        ss_code: key,
        location: inst.location || '',
        instance_id: inst.id,
        unitCount: 0,
        activeCount: 0,
        ss_name: inst.ss_name,
        nextDueDate: inst.nextDueDate || inst.next_due_date,
        inspection_frequency: inst.inspection_frequency,
        default_assignee_name: inst.default_assignee_name,
        default_assignee_type: inst.default_assignee_type,
        default_assignee_email: inst.default_assignee_email,
        default_worker_id: inst.default_worker_id,
      });
    }
    for (const u of scopedUnitsAll) {
      const key = String(u.ss_code || '').trim();
      if (!key || !byCode.has(key)) continue;
      const row = byCode.get(key);
      row.unitCount += 1;
      row.activeCount += 1;
    }
    return [...byCode.values()]
      .map((row) => ({
        ...row,
        hasOpenJob: hasOpenInspection(jobsBySs.get(row.ss_code) || []),
      }))
      .sort((a, b) => compareSsCode(a.ss_code, b.ss_code));
  }, [scopedInstances, scopedUnitsAll, jobsBySs]);

  const selectedSs = useMemo(
    () => ssRows.find((r) => sameSsCode(r.ss_code, ssFilter)) || null,
    [ssRows, ssFilter]
  );

  const unitsForSs = useMemo(() => {
    if (!ssFilter) return [];
    return scopedUnitsAll.filter((u) => sameSsCode(u.ss_code, ssFilter));
  }, [scopedUnitsAll, ssFilter]);

  const toggleCode = (code, on) => {
    setSelectedCodes((prev) => {
      const next = new Set(prev);
      if (on) next.add(code);
      else next.delete(code);
      return next;
    });
  };

  const toggleAllVisible = (on) => {
    setSelectedCodes(on ? new Set(ssRows.map((r) => r.ss_code)) : new Set());
  };

  if (buildingLoading) {
    return <div className="text-center py-12 text-gray-500">Loading…</div>;
  }

  if (!building) {
    return <div className="text-center py-12">Building not found</div>;
  }

  return (
    <div>
      <BuildingPageHeading
        buildingId={id}
        buildingName={building.name}
        title={ssFilter ? `${formatSsLabel(ssFilter)} units` : 'SS Units'}
        infoTitle="SS Units"
        infoChildren={
          <div className="space-y-2 text-sm">
            <p className="font-medium">{building.name}</p>
            <p>
              Tick SS, then Schedule selected / Schedule all ?? each SS goes to its default IQP app dashboard.
              Add or remove SS in Edit Building ??Specified Systems. Full/partial inspection scope is set there too.
              {isPartialInspectionScope(building)
                ? ' Showing SS in this building?�s partial inspection scope only.'
                : null}
            </p>
          </div>
        }
        actions={
          ssFilter && !setupList ? (
            <button
              type="button"
              onClick={() => document.getElementById('ss-unit-subtype-save')?.click()}
              className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              Save
            </button>
          ) : null
        }
      />

      {!ssFilter ? (
        <div className="bg-card rounded-lg shadow overflow-hidden">
          {unitsLoading ? (
            <p className="p-8 text-center text-gray-500">Loading…</p>
          ) : ssRows.length === 0 ? (
            <p className="p-8 text-center text-gray-500">
              No specified systems in scope. Add SS in <strong>Edit Building</strong> ??Specified Systems,
              then return here to set unit types.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-3 w-10">
                      <input
                        type="checkbox"
                        checked={ssRows.length > 0 && selectedCodes.size === ssRows.length}
                        onChange={(e) => toggleAllVisible(e.target.checked)}
                        aria-label="Select all SS"
                      />
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Specified System
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Location
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Default IQP
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Unit IDs
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {ssRows.map((row) => {
                    const chip = formatAssigneeChip(row, { compact: true });
                    return (
                      <tr
                        key={row.ss_code}
                        onClick={() => setSearchParams({ ss: row.ss_code })}
                        className="hover:bg-primary-50 cursor-pointer"
                      >
                        <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={selectedCodes.has(row.ss_code)}
                            onChange={(e) => toggleCode(row.ss_code, e.target.checked)}
                            aria-label={`Select ${row.ss_code}`}
                          />
                        </td>
                        <td className="px-4 py-3 text-sm font-semibold text-gray-900">
                          {formatSsLabel(row.ss_code)}
                          {row.hasOpenJob ? (
                            <span
                              className="ml-2 text-[10px] font-medium uppercase text-emerald-700"
                              title="Open inspection work pack exists for this SS (not paperwork)"
                            >
                              Open job
                            </span>
                          ) : null}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">{row.location || '—'}</td>
                        <td className="px-4 py-3 text-sm">
                          {chip.empty ? (
                            <span className="text-gray-400">…</span>
                          ) : (
                            <span className="font-medium text-gray-800">{chip.label}</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-primary-700 font-medium">
                          {row.unitCount}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : (
        <SsUnitSubtypeWizard
          buildingId={id}
          ssCode={selectedSs?.ss_code || ssFilter}
          instanceId={selectedSs?.instance_id}
          units={unitsForSs}
          setupList={setupList}
          onShowList={() => setSearchParams({ ss: ssFilter, setup: 'list' })}
          onBackToPick={() => setSearchParams({ ss: ssFilter })}
          onOpenUnit={(unitId, typeCode, typeLabel) => {
            navigate(`/buildings/${id}/units/${unitId}`, {
              state: {
                origin: {
                  to: `/buildings/${id}/units?ss=${encodeURIComponent(ssFilter)}&setup=list`,
                  label: 'Select SS Units Setting',
                },
                setupTypeCode: typeCode,
                setupTypeLabel: typeLabel,
              },
            });
          }}
        />
      )}

      {scheduleItems ? (
        <ScheduleToIqpModal
          buildingId={id}
          buildingName={building.name}
          items={scheduleItems.map((r) => ({
            ss_code: r.ss_code,
            ss_name: r.ss_name || r.location || r.ss_code,
            due_date: r.nextDueDate,
            default_worker_id: r.default_worker_id,
            default_assignee_name: r.default_assignee_name,
          }))}
          onClose={() => {
            setScheduleItems(null);
            setSelectedCodes(new Set());
          }}
        />
      ) : null}
    </div>
  );
}

export default BuildingUnits;
